import React, { useState } from 'react';
import { notificationService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import CanvasSubmissionModal from '../components/admin/CanvasSubmissionModal';
import { Bell, CheckCheck, Trash2, AlertCircle, ExternalLink, ClipboardCheck } from 'lucide-react';
import toast from 'react-hot-toast';

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const NotificationsPage = () => {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [reviewingSubmissionId, setReviewingSubmissionId] = useState(null);

  const { data, loading, error, refetch } = useApi(
    () => notificationService.getAll({ page: 1, limit: 30, unreadOnly }),
    [unreadOnly],
  );

  const notifications = Array.isArray(data) ? data
    : Array.isArray(data?.notifications) ? data.notifications
    : Array.isArray(data?.data) ? data.data : [];

  const handleMarkRead = async (id) => {
    try { await notificationService.markRead(id); refetch(); }
    catch { toast.error('Could not mark as read'); }
  };

  const handleMarkAll = async () => {
    try {
      await notificationService.markAllRead();
      toast.success('All notifications marked as read');
      refetch();
    } catch { toast.error('Could not mark all as read'); }
  };

  const handleDelete = async (id) => {
    try { await notificationService.delete(id); refetch(); }
    catch { toast.error('Could not delete notification'); }
  };

  const handleOpenCanvasReview = (n) => {
    let submissionId = null;
    try { submissionId = JSON.parse(n.data || '{}').submissionId; } catch {}
    if (!submissionId) { toast.error('No submission linked to this notification'); return; }
    setReviewingSubmissionId(submissionId);
    if (!n.isRead) handleMarkRead(n.id);
  };

  return (
    <DashboardLayout title="Notifications">
      <div className="max-w-2xl space-y-5">

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {[false, true].map((v) => (
              <button key={String(v)} onClick={() => setUnreadOnly(v)}
                className={`text-xs font-medium px-4 py-2 rounded-full border transition-all ${unreadOnly === v ? 'bg-forest-800 text-cream-50 border-forest-800' : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'}`}>
                {v ? 'Unread Only' : 'All'}
              </button>
            ))}
          </div>
          <button onClick={handleMarkAll} className="flex items-center gap-1.5 text-xs text-forest-500 hover:text-forest-800 font-medium transition-colors">
            <CheckCheck size={14} /> Mark all read
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} />
            <span className="text-sm">Could not load notifications.</span>
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <div className="text-center py-20 text-forest-400 bg-white rounded-2xl border border-cream-200">
            <Bell size={40} className="mx-auto mb-4 opacity-30" />
            <p className="font-serif text-xl text-forest-600">No notifications</p>
            <p className="text-sm mt-2">You're all caught up!</p>
          </div>
        )}

        <div className="space-y-2">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white border border-cream-200 rounded-2xl p-4 animate-pulse flex gap-3">
                  <div className="w-2 h-2 bg-cream-200 rounded-full mt-2 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-cream-200 rounded w-3/4" />
                    <div className="h-3 bg-cream-100 rounded w-1/4" />
                  </div>
                </div>
              ))
            : notifications.map((n) => (
                <div key={n.id}
                  className={`bg-white border rounded-2xl p-4 flex items-start gap-3 group transition-all ${!n.isRead ? 'border-forest-200 bg-forest-50/30' : 'border-cream-200'}`}>
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!n.isRead ? 'bg-forest-600' : 'bg-cream-200'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold mb-0.5 ${!n.isRead ? 'text-forest-900' : 'text-forest-700'}`}>
                      {n.title ?? 'Notification'}
                    </p>
                    {/* Strip any raw URL from the message before displaying */}
                    {n.type !== 'SESSION_READY' && (
                      <p
                        onClick={n.type === 'CANVAS_SUBMITTED' ? () => handleOpenCanvasReview(n) : undefined}
                        className={`text-sm leading-relaxed ${!n.isRead ? 'text-forest-700' : 'text-forest-500'} ${n.type === 'CANVAS_SUBMITTED' ? 'cursor-pointer hover:text-forest-900' : ''}`}>
                        {(n.message ?? '').replace(/https?:\/\/\S+/g, '').trim()}
                      </p>
                    )}
                    {n.type === 'CANVAS_SUBMITTED' && (
                      <button onClick={() => handleOpenCanvasReview(n)}
                        className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium bg-forest-700 text-cream-50 px-3 py-1.5 rounded-full hover:bg-forest-800 transition-colors">
                        <ClipboardCheck size={11} /> Review Submission →
                      </button>
                    )}
                    {/* SESSION_READY — clean message + Join button */}
                    {n.type === 'SESSION_READY' && (() => {
                      // Get link from data JSON, or extract from old-style message
                      let link = null;
                      try { link = JSON.parse(n.data || '{}').meetingLink; } catch {}
                      if (!link) {
                        const match = (n.message || '').match(/https?:\/\/\S+/);
                        if (match) link = match[0];
                      }
                      return (
                        <>
                          <p className={`text-sm leading-relaxed ${!n.isRead ? 'text-forest-700' : 'text-forest-500'}`}>
                            Your session is ready to start.
                          </p>
                          {link && (
                            <a href={link} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium bg-forest-700 text-cream-50 px-3 py-1.5 rounded-full hover:bg-forest-800 transition-colors">
                              <ExternalLink size={11} /> Join Session →
                            </a>
                          )}
                        </>
                      );
                    })()}
                    <span className="text-xs text-forest-400 mt-1 block">{timeAgo(n.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    {!n.isRead && (
                      <button onClick={() => handleMarkRead(n.id)} title="Mark as read"
                        className="p-1.5 text-forest-400 hover:text-forest-700 hover:bg-cream-100 rounded-lg transition-colors">
                        <CheckCheck size={13} />
                      </button>
                    )}
                    <button onClick={() => handleDelete(n.id)} title="Delete"
                      className="p-1.5 text-forest-300 hover:text-rust-500 hover:bg-rust-50 rounded-lg transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
        </div>
      </div>

      {reviewingSubmissionId && (
        <CanvasSubmissionModal
          submissionId={reviewingSubmissionId}
          onClose={() => setReviewingSubmissionId(null)}
          onGraded={() => setReviewingSubmissionId(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default NotificationsPage;